"use client"

import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/apiService';
import { DashboardHeader } from '../components/ui/dashboard-header';
import { DeviceStatsTable } from '../components/ui/device-stats-table';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { FlaskConical, Server, Search } from 'lucide-react';
import { CustomOverlaySidebar } from '../components/ui/custom-overlay-sidebar';
import { trialSerialSet, isTrialStat } from '../utils/trialMachines';
import { useLogout } from '../hooks/useLogout';
import { useSidebar } from '../hooks/useSidebar';
import type { DeviceStats, Machine } from '../types';

const TrialMachinesPage: React.FC = () => {
  const [stats, setStats] = useState<DeviceStats[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sidebarOpen, closeSidebar, toggleSidebar } = useSidebar();
  const handleLogout = useLogout();
  const [serialSearch, setSerialSearch] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [machinesResponse, statsResponse] = await Promise.all([
        apiService.getMachines(),
        apiService.getDeviceStats({ limit: 1000 }),
      ]);

      if (machinesResponse.success && Array.isArray(machinesResponse.data)) {
        setMachines(machinesResponse.data);
      }

      if (statsResponse.success) {
        const raw = statsResponse.data as any;
        const arr = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.results)
            ? raw.results
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
        const list = arr.map((item: any) => ({
          ...item,
          additional_notes: item.additional_notes || item.additional || {},
          additional: item.additional || item.additional_notes || {},
        }));
        setStats(list);
        setError(null);
      } else {
        setError(statsResponse.error || 'Failed to fetch stats');
      }
    } catch {
      setError('An unexpected error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const trialSerials = useMemo(() => trialSerialSet(machines), [machines]);

  // Only ServiceStats rows belonging to trial machines.
  const trialStats = useMemo(
    () => stats.filter((stat) => isTrialStat(stat, trialSerials)),
    [stats, trialSerials]
  );

  const trialMachineCount = trialSerials.size;
  const reportingSerials = useMemo(
    () => new Set(trialStats.map((s) => s.site_id)),
    [trialStats]
  );

  // Trial machines report with site_id = serial_number (see Machine.get_effective_site_id).
  const filteredTrialStats = useMemo(() => {
    const query = serialSearch.trim().toLowerCase();
    if (!query) return trialStats;
    return trialStats.filter((s) => s.site_id.toLowerCase().includes(query));
  }, [trialStats, serialSearch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ backgroundColor: '#0f0f0f', color: '#e2e8f0' }}>
        <h2>Loading trial machines...</h2>
      </div>
    );
  }

  const SectionHeader = ({ title }: { title: string }) => (
    <div className="flex items-center gap-3 mb-5">
      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
        {title}
      </span>
      <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }} />
    </div>
  );

  const metricCardBase: React.CSSProperties = { boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' };

  return (
    <div style={{ backgroundColor: '#0f0f0f', minHeight: '100vh' }}>
      <CustomOverlaySidebar isOpen={sidebarOpen} onClose={closeSidebar} />

      <div className="w-full min-h-screen">
        <DashboardHeader
          onLogout={handleLogout}
          onSidebarToggle={toggleSidebar}
          title="Trial Machines"
          showFilters={false}
        />

        <div className="p-6 space-y-10">
          {/* Overview */}
          <div>
            <SectionHeader title="Trial Overview" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card
                className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-2xl"
                style={{ background: 'linear-gradient(135deg, #3a2a00 0%, #5a4300 100%)', ...metricCardBase }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="rounded-xl p-2.5" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                      <FlaskConical className="h-5 w-5" style={{ color: '#ffffff' }} />
                    </div>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', color: '#ffffff' }}
                    >
                      Trial
                    </span>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'rgba(255, 255, 255, 0.55)' }}>
                    Trial Machines
                  </div>
                  <div className="text-5xl font-bold leading-none" style={{ color: '#ffffff' }}>
                    {trialMachineCount}
                  </div>
                </CardContent>
              </Card>

              <Card
                className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-2xl"
                style={{ background: 'linear-gradient(135deg, #003433 0%, #005a58 100%)', ...metricCardBase }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="rounded-xl p-2.5" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                      <Server className="h-5 w-5" style={{ color: '#ffffff' }} />
                    </div>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', color: '#ffffff' }}
                    >
                      Reporting
                    </span>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'rgba(255, 255, 255, 0.55)' }}>
                    Reporting Trial Machines
                  </div>
                  <div className="text-5xl font-bold leading-none" style={{ color: '#ffffff' }}>
                    {reportingSerials.size}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Trial machine activity */}
          <div>
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <SectionHeader title="Trial Machine Activity" />
              </div>
              {trialMachineCount > 0 && (
                <div className="relative flex-shrink-0" style={{ width: '16rem' }}>
                  <Search
                    className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(255,255,255,0.4)' }}
                  />
                  <Input
                    placeholder="Search by serial number..."
                    value={serialSearch}
                    onChange={(e) => setSerialSearch(e.target.value)}
                    className="h-9 pl-9 text-sm rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.85)',
                    }}
                  />
                </div>
              )}
            </div>
            {trialMachineCount === 0 ? (
              <div
                className="text-center py-10 rounded-xl"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', border: '1px dashed rgba(255, 255, 255, 0.2)' }}
              >
                <div className="text-base font-semibold" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>No trial machines yet</div>
                <div className="text-sm mt-2" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Machines appear here on first contact via the trial license endpoint, and move to the main dashboard once activated.
                </div>
              </div>
            ) : filteredTrialStats.length === 0 ? (
              <div
                className="text-center py-10 rounded-xl"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', border: '1px dashed rgba(255, 255, 255, 0.2)' }}
              >
                <div className="text-base font-semibold" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>No matches</div>
                <div className="text-sm mt-2" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  No trial machine with a serial number matching "{serialSearch}".
                </div>
              </div>
            ) : (
              <DeviceStatsTable
                stats={filteredTrialStats}
                loading={loading}
                error={error}
                onRefresh={fetchData}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialMachinesPage;
